/**
 * Ink-brush strokes as pixel art (C0-VFX; method in docs/research/C0_VFX.md).
 *
 * A stroke is a spline through a few points with a width profile (起笔 the press, 行笔 the travel,
 * 收笔 the lift or the blunt return) and bristles that run dry toward its end (飞白 flying white: the
 * stroke breaks into streaks). It is rasterised once into a field of cells on the figures' grid
 * (two canvas pixels per art pixel, as the rig draws them), each cell knowing how far along the
 * stroke it is (`s`) and its role (interior, lit sheen, dark edge, accent halo). A frame of the
 * stroke is then a reveal (painted up to `s`), a tail (wiped from the start, for strokes that
 * travel) and a fade (ink bleeding to a pale wash, then dissolving in a dither from the tail).
 * Frames are quantised and cached as small immutable canvases, so drawing costs one drawImage.
 */
import { canvas, ctx2d } from '../pixel';
import { hash2, noise1, smoothstep } from './noise';
import { INK_PALETTES, word, type InkTone, type InkPalette } from './palette';

export type Pt = readonly [number, number];

/** Width along the stroke: press-travel-lift, a thin flick, a blunt bar, an even line, a teardrop. */
export type StrokeShape = 'press' | 'flick' | 'blunt' | 'even' | 'drop' | 'swell';

export interface Stroke {
  /** Control points, art pixels; the stroke is a Catmull-Rom spline through them. */
  pts: readonly Pt[];
  /** Widest width, art pixels. */
  w: number;
  shape?: StrokeShape;
  /** 0–1: how much the bristles run dry toward the end (flying white). */
  dry?: number;
  /** What the body is painted in: ink with an accent halo, the accent itself, or a pale wash. */
  body?: 'ink' | 'acc' | 'wash';
  /** No accent halo round an ink body. */
  noHalo?: boolean;
  seed?: number;
}

/** Canvas pixels per art pixel (the rig's `RIG_RES`). */
export const RES = 2;

/**
 * The qi look (C0-VFX-PREP, the user 2026-10-10: the black ink-brush slashes go): strokes and drops of the move tones
 * are drawn as qi light; blood and earth keep their own look. `false` brings the ink A look back.
 */
export const QI_LOOK = true;

/**
 * The brush look as a whole (the user 2026-10-11: no brush or ink-stroke look in the game, on hit, cast or travel):
 * while off, every brush stroke and ink drop of the ink set draws nothing; the pixel parts (flying swords, the guard
 * shield, the detail layer's hard-pixel motifs) stay, and every landed blow gets a plain pixel impact and pixel blood
 * instead (render/ink/pixelHit.ts, wired in render/ink/game.ts). `true` brings the brush back.
 */
export const BRUSH_LOOK = false;
/** The switch, or (dev, before/after captures) `window.__brushLook = true | false` over it. */
export const brushOn = (): boolean => {
  const o = import.meta.env?.DEV && typeof window !== 'undefined' ? (window as unknown as { __brushLook?: boolean }).__brushLook : undefined;
  return o ?? BRUSH_LOOK;
};
export const qiLook = (kind: string): boolean => QI_LOOK && kind !== 'blood' && kind !== 'earth';

const enum Role {
  In = 1,
  InLit = 2,
  Rim = 3,
  Halo = 4,
  HaloLit = 5,
}

interface Field {
  /** Bounding box in canvas pixels, relative to the stroke's origin. */
  x0: number;
  y0: number;
  w: number;
  h: number;
  /** Per cell (row-major, w*h): role (0 empty), 0–255 along, 0–255 hash. */
  role: Uint8Array;
  s: Uint8Array;
  hs: Uint8Array;
  body: 'ink' | 'acc' | 'wash';
}

function profile(shape: StrokeShape, s: number): number {
  switch (shape) {
    case 'press': {
      // The brush set down hard (a slight bulb), carried, then lifted to a point.
      const bulb = s < 0.12 ? 0.55 + 0.6 * Math.sin((s / 0.12) * (Math.PI / 2)) : 1.15 - 0.45 * smoothstep(0.12, 0.7, s);
      return s > 0.72 ? bulb * Math.pow(Math.max(0, (1 - s) / 0.28), 0.75) : bulb;
    }
    case 'flick':
      return s < 0.3 ? 0.25 + 0.75 * smoothstep(0, 0.3, s) : Math.pow(Math.max(0, (1 - s) / 0.7), 0.6);
    case 'blunt':
      return 0.85 + 0.15 * Math.sin(s * Math.PI) - (s > 0.94 ? (s - 0.94) * 4 : 0);
    case 'even':
      return 0.8;
    case 'drop':
      return Math.sin(Math.min(1, s * 1.15) * Math.PI) ** 0.6;
    case 'swell':
      return 0.3 + 0.7 * Math.sin(s * Math.PI);
  }
}

/** Catmull-Rom spline through the points, sampled every `step` units of length. */
function spline(pts: readonly Pt[], step: number): { x: number; y: number; tx: number; ty: number; len: number }[] {
  const P = [pts[0]!, ...pts, pts[pts.length - 1]!];
  const raw: [number, number][] = [];
  for (let i = 1; i < P.length - 2; i++) {
    const [p0, p1, p2, p3] = [P[i - 1]!, P[i]!, P[i + 1]!, P[i + 2]!];
    const seg = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
    const n = Math.max(2, Math.ceil(seg / (step * 0.5)));
    for (let j = 0; j < n; j++) {
      const t = j / n;
      const t2 = t * t;
      const t3 = t2 * t;
      const f = (a: number, b: number, c: number, d: number) =>
        0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      raw.push([f(p0[0], p1[0], p2[0], p3[0]), f(p0[1], p1[1], p2[1], p3[1])]);
    }
  }
  raw.push([pts[pts.length - 1]![0], pts[pts.length - 1]![1]]);
  const out: { x: number; y: number; tx: number; ty: number; len: number }[] = [];
  let len = 0;
  for (let i = 0; i < raw.length; i++) {
    const a = raw[Math.max(0, i - 1)]!;
    const b = raw[Math.min(raw.length - 1, i + 1)]!;
    const d = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    if (i > 0) len += Math.hypot(raw[i]![0] - raw[i - 1]![0], raw[i]![1] - raw[i - 1]![1]);
    out.push({ x: raw[i]![0], y: raw[i]![1], tx: (b[0] - a[0]) / d, ty: (b[1] - a[1]) / d, len });
  }
  return out;
}

const FIELDS = new WeakMap<Stroke, Field>();

function fieldOf(st: Stroke): Field {
  const have = FIELDS.get(st);
  if (have) return have;
  const shape = st.shape ?? 'press';
  // The qi look keeps a little dry streaking at the very end (speed), not the bristle texture of a brush.
  const dry = (st.dry ?? 0.35) * (QI_LOOK ? 0.3 : 1);
  const seed = st.seed ?? 1;
  const body = st.body ?? 'ink';
  const halo = body === 'ink' && !st.noHalo;
  const samples = spline(
    st.pts.map(([x, y]) => [x * RES, y * RES] as Pt),
    0.5,
  );
  const total = samples[samples.length - 1]!.len || 1;
  const hwMax = (st.w * RES) / 2;
  const bands = Math.max(3, Math.min(9, Math.round(st.w * 1.2)));
  const cells = new Map<number, number>();
  const key = (x: number, y: number) => ((y + 4096) << 13) | (x + 4096);
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of samples) {
    const s = p.len / total;
    const rag = 1 + 0.18 * (noise1((p.len / RES) * 0.45, seed) - 0.5);
    const hw = Math.max(0.6, hwMax * profile(shape, s) * rag);
    const nx = -p.ty;
    const ny = p.tx;
    for (let o = -hw; o <= hw; o += 0.5) {
      const u = o / hw;
      const band = Math.min(bands - 1, Math.floor(((u + 1) / 2) * bands));
      // A bristle running dry: gaps along it, more of them toward the end of the stroke.
      const thirst = dry * smoothstep(0.3, 1, s) * (0.55 + 0.9 * hash2(band, 3, seed));
      if (thirst > 0 && noise1((p.len / RES) * 0.55 + band * 13.7, seed + band) < thirst) continue;
      const cx = Math.round(p.x + nx * o);
      const cy = Math.round(p.y + ny * o);
      const k = key(cx, cy);
      if (!cells.has(k)) cells.set(k, s);
      if (cx < x0) x0 = cx;
      if (cy < y0) y0 = cy;
      if (cx > x1) x1 = cx;
      if (cy > y1) y1 = cy;
    }
  }
  const pad = halo ? 3 : 1;
  x0 -= pad;
  y0 -= pad;
  const w = x1 - x0 + 1 + pad;
  const h = y1 - y0 + 1 + pad;
  const role = new Uint8Array(w * h);
  const sv = new Uint8Array(w * h);
  const hs = new Uint8Array(w * h);
  for (const [k, s] of cells) {
    const cx = (k & 8191) - 4096 - x0;
    const cy = (k >> 13) - 4096 - y0;
    role[cy * w + cx] = Role.In;
    sv[cy * w + cx] = Math.round(s * 255);
  }
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h ? 0 : role[y * w + x]!);
  // Edges: interior cells with an empty side get the dark edge; next to an upper-right edge, the sheen.
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (role[i] !== Role.In) continue;
      if (!at(x - 1, y) || !at(x + 1, y) || !at(x, y - 1) || !at(x, y + 1)) role[i] = Role.Rim;
    }
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (role[i] === Role.In && (at(x + 1, y - 1) === Role.Rim || at(x + 2, y - 2) === Role.Rim) && at(x + 1, y - 1) !== 0)
        role[i] = Role.InLit;
    }
  // The accent halo: one art pixel (two cells) round an ink body; lit on the upper right.
  if (halo)
    for (let pass = 0; pass < 2; pass++) {
      const add: number[] = [];
      for (let y = 0; y < h; y++)
        for (let x = 0; x < w; x++) {
          if (role[y * w + x]) continue;
          let best = -1;
          let lit = false;
          for (const [dx, dy] of [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
          ] as const) {
            const r = at(x + dx, y + dy);
            if (r && (pass === 0 ? r <= Role.Rim : true)) {
              best = sv[(y + dy) * w + x + dx]!;
              if (dx < 0 || dy > 0) lit = true;
            }
          }
          if (best >= 0) add.push(y * w + x, best, lit ? 1 : 0);
        }
      for (let j = 0; j < add.length; j += 3) {
        role[add[j]!] = add[j + 2] ? Role.HaloLit : Role.Halo;
        sv[add[j]!] = add[j + 1]!;
      }
    }
  for (let i = 0; i < w * h; i++) hs[i] = Math.floor(hash2(i % w, Math.floor(i / w), seed + 99) * 255);
  const f: Field = { x0, y0, w, h, role, s: sv, hs, body };
  FIELDS.set(st, f);
  return f;
}

/** Steps a frame's reveal, tail and fade are rounded to (the frames are cached by them). */
const Q = 40;
const QF = 20;
const FRAMES = new WeakMap<Stroke, Map<string, HTMLCanvasElement>>();
let cached = 0;

function frame(st: Stroke, kind: InkTone, reveal: number, tail: number, fade: number): HTMLCanvasElement | null {
  const rq = Math.round(Math.max(0, Math.min(1, reveal)) * Q);
  const tq = Math.round(Math.max(0, Math.min(1, tail)) * Q);
  const fq = Math.round(Math.max(0, Math.min(1, fade)) * QF);
  if (rq === 0 || tq >= Q || fq >= QF) return null;
  let m = FRAMES.get(st);
  if (!m) FRAMES.set(st, (m = new Map()));
  const k = `${kind}${rq}.${tq}.${fq}`;
  const have = m.get(k);
  if (have) return have;
  const f = fieldOf(st);
  const P: InkPalette = INK_PALETTES[kind];
  const c = canvas(f.w, f.h);
  const g = ctx2d(c);
  const img = g.createImageData(f.w, f.h);
  const o = new Uint32Array(img.data.buffer);
  const r = (rq / Q) * 255;
  const tl = (tq / Q) * 255;
  const fd = fq / QF;
  // The qi look (the user, 2026-10-10: "remove the ink-brush pattern"): an ink body is drawn as light in the kind's
  // accent instead (light interior, white sheen, accent rim, the accent's dark as the outermost outline), the way the
  // golden dragon palm reads; a wash echo is a pale accent band; fading turns to the accent, not grey ink.
  const qi = qiLook(kind);
  const cols = qi
    ? f.body === 'wash'
      ? [0, word(P.acc), word(P.accLit), word(P.accDark), 0, 0]
      : f.body === 'ink'
        ? [0, word(P.accLit), word('#ffffff'), word(P.acc), word(P.accDark), word(P.acc)]
        : [0, word(P.acc), word(P.accLit), word(P.accDark), 0, 0]
    : f.body === 'ink'
      ? [0, word(P.ink), word(P.inkLit), word(P.edge), word(P.acc), word(P.accLit)]
      : f.body === 'acc'
        ? [0, word(P.acc), word(P.accLit), word(P.accDark), 0, 0]
        : [0, word(P.wash), word(P.wash), word(P.ink), 0, 0];
  const wash = word(qi ? P.acc : P.wash);
  const tip = word(P.accLit);
  const tipBand = r < 255 ? 14 : 0;
  for (let i = 0; i < o.length; i++) {
    const role = f.role[i]!;
    if (!role) continue;
    const s = f.s[i]!;
    if (s > r) continue;
    const h = f.hs[i]! / 255;
    // The tail wiped from the start (a travelling stroke), ragged.
    if (tl > 0 && s < tl + h * 18 - 9) continue;
    const halo = role >= Role.Halo;
    // Fading: the halo goes first, then the ink dissolves from the tail end in a dither.
    if (fd > 0) {
      if (halo && (fd > 0.3 || h < fd * 3.3)) continue;
      if (h < fd * 1.5 - 0.5 * (1 - s / 255)) continue;
    }
    let col = cols[role]!;
    if (!col) continue;
    // The live brush tip glows wet in the accent's light tone.
    if (tipBand && s > r - tipBand && !halo) col = tip;
    // Ink bleeding into the paper: interior cells turn to the pale wash as it fades.
    else if (fd > 0 && f.body === 'ink' && role <= Role.InLit && h < fd * 1.6) col = wash;
    o[i] = col;
  }
  g.putImageData(img, 0, 0);
  if (cached++ > 4000) {
    // Drop every cached frame now and then; they are rebuilt on demand (cheap, a few hundred cells).
    cached = 0;
    FRAMES.delete(st);
  }
  m.set(k, c);
  return c;
}

/**
 * Draws a stroke at art position (`x`, `y`) (the origin its points are relative to): painted up to
 * `reveal` (0–1), wiped from the start up to `tail`, faded by `fade`. `flip` mirrors it about `x`.
 */
export function drawStroke(
  g: CanvasRenderingContext2D,
  st: Stroke,
  kind: InkTone,
  x: number,
  y: number,
  reveal: number,
  fade = 0,
  flip = false,
  tail = 0,
): void {
  if (!brushOn()) return;
  const c = frame(st, kind, reveal, tail, fade);
  if (!c) return;
  const f = fieldOf(st);
  const X = Math.round(x * RES) / RES;
  const Y = Math.round(y * RES) / RES;
  if (!flip) {
    g.drawImage(c, X + f.x0 / RES, Y + f.y0 / RES, f.w / RES, f.h / RES);
    return;
  }
  g.save();
  g.translate(X, Y);
  g.scale(-1, 1);
  g.drawImage(c, f.x0 / RES, f.y0 / RES, f.w / RES, f.h / RES);
  g.restore();
}

/** Prepares a stroke's cells ahead of use (so the first frame of a move costs nothing extra). */
export function warmStroke(st: Stroke): void {
  fieldOf(st);
}

const DROPS = new Map<string, HTMLCanvasElement>();

/** An ink drop of radius `r` art pixels (1–4): a dark rim, the body, a wet glint on the upper right. */
export function drawDrop(
  g: CanvasRenderingContext2D,
  kind: InkTone,
  x: number,
  y: number,
  r: number,
  tone: 'ink' | 'acc' = 'ink',
): void {
  if (!brushOn()) return;
  const rr = Math.max(1, Math.min(4, Math.round(r * 2) / 2));
  const k = `${kind}${tone}${rr}`;
  let c = DROPS.get(k);
  if (!c) {
    const P = INK_PALETTES[kind];
    const n = Math.ceil(rr * RES) * 2 + 2;
    c = canvas(n, n);
    const gg = ctx2d(c);
    const m = n / 2;
    const R = rr * RES;
    for (let yy = 0; yy < n; yy++)
      for (let xx = 0; xx < n; xx++) {
        const d = Math.hypot(xx + 0.5 - m, yy + 0.5 - m);
        if (d > R) continue;
        const lit = xx + 0.5 - m > R * 0.15 && m - (yy + 0.5) > R * 0.15 && d < R - 0.8;
        gg.fillStyle =
          tone === 'ink' && !qiLook(kind)
            ? d > R - 1
              ? P.acc
              : lit && R > 1.5
                ? P.inkLit
                : P.ink
            : d > R - 1
              ? P.accDark
              : lit
                ? P.accLit
                : P.acc;
        gg.fillRect(xx, yy, 1, 1);
      }
    DROPS.set(k, c);
  }
  const s = c.width / RES;
  g.drawImage(c, Math.round((x - s / 2) * RES) / RES, Math.round((y - s / 2) * RES) / RES, s, s);
}
